// Package imports:
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { HexColorPicker } from 'react-colorful';

interface IColorPickerFieldProps {
    label: string;
    value: string;
    onChange: (color: string) => void;
}

const HEX_REGEX = /^#[0-9a-fA-F]{6}$/;

const ColorPickerField: React.FC<IColorPickerFieldProps> = ({ label, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value || '');
    const popoverRef = useRef<HTMLDivElement>(null);

    // Sync input when value prop changes externally
    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    // Close popover on outside click
    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const handlePickerChange = useCallback((color: string) => {
        setInputValue(color);
        onChange(color);
    }, [onChange]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value;
        // Auto-prefix # if user types hex without it
        if (val && !val.startsWith('#')) {
            val = '#' + val;
        }
        setInputValue(val);
        if (HEX_REGEX.test(val)) {
            onChange(val);
        }
    }, [onChange]);

    const handleInputBlur = useCallback(() => {
        // If invalid, revert to last valid value or clear
        if (inputValue && !HEX_REGEX.test(inputValue)) {
            setInputValue(value || '');
        }
    }, [inputValue, value]);

    const displayColor = HEX_REGEX.test(value) ? value : '#4569EE';

    return (
        <div className="color-picker-field" ref={popoverRef}>
            <label className="color-picker-field__label">{label}</label>
            <div className="color-picker-field__controls">
                <button
                    type="button"
                    className="color-picker-field__swatch"
                    style={{ backgroundColor: displayColor }}
                    onClick={() => setIsOpen(!isOpen)}
                    aria-label={`Velja ${label.toLowerCase()}`}
                />
                <input
                    type="text"
                    className="color-picker-field__input"
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onFocus={() => setIsOpen(true)}
                    placeholder="#000000"
                    maxLength={7}
                    spellCheck={false}
                />
            </div>
            {isOpen && (
                <div className="color-picker-field__popover">
                    <HexColorPicker color={displayColor} onChange={handlePickerChange} />
                </div>
            )}
        </div>
    );
};

export default ColorPickerField;
